import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CheckCircleOutline as CheckIcon,
  PlayArrow as PlayArrowIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config';
import LivePractice from './LivePractice';
import { useLanguage } from '../contexts/LanguageContext';
import usePracticeLocalization from '../hooks/usePracticeLocalization';
import { ENGLISH_SCENARIOS, localizeScenarioTitle } from '../i18n/scenarios';


export default function Practice() {
  const navigate = useNavigate();
  const { tr, t, isEnglish } = useLanguage();
  const [searchParams] = useSearchParams();
  const category = searchParams.get('category') || localStorage.getItem('selectedCategory') || '銀行電銷';
  const preferredScenario = category === '銀行電銷' ? 'case11' : 'case15';
  const [scenarios, setScenarios] = useState([]);
  const [selectedId, setSelectedId] = useState(preferredScenario);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [started, setStarted] = useState(false);
  const [sessionKey, setSessionKey] = useState(0);

  useEffect(() => {
    let active = true;
    axios.get(`${API_URL}/api/live-practice/scenarios`)
      .then((response) => {
        if (!active) return;
        setScenarios(response.data || []);
        if (!(response.data || []).some((scenario) => scenario.id === preferredScenario)) {
          setSelectedId(response.data?.[0]?.id || '');
        }
      })
      .catch(() => active && setError(tr('無法載入實戰演練情境。', 'Could not load the live practice scenarios.')))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
    // tr only changes with the UI language; the scenario list itself is language-neutral
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preferredScenario]);

  const selected = useMemo(
    () => scenarios.find((scenario) => scenario.id === selectedId),
    [scenarios, selectedId],
  );
  // Scenario copy (scene, tasks) is Chinese source data; show the English version in the English UI
  const { translateContent, isLocalizationLoading } = usePracticeLocalization(selected?.id);
  const contentPending = isEnglish && isLocalizationLoading;
  const nameOf = (scenario) => (
    isEnglish ? (ENGLISH_SCENARIOS[scenario?.id]?.name || translateContent(scenario?.name)) : scenario?.name
  );
  const categoryOf = (scenario) => (scenario?.category ? t(`category.${scenario.category}`) : '');

  if (started && selected) {
    return (
      <LivePractice
        key={`${selected.id}-${sessionKey}`}
        scenario={selected}
        onExit={() => setStarted(false)}
        onRetry={() => setSessionKey((current) => current + 1)}
      />
    );
  }

  return (
    <Box sx={{ position: 'fixed', inset: 0, zIndex: 1450, overflowY: 'auto', bgcolor: '#f4f7fb' }}>
      <Box sx={{ maxWidth: 1080, mx: 'auto', px: { xs: 2, md: 4 }, py: 3, minHeight: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box component="header" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <IconButton onClick={() => navigate(`/scenarios?category=${encodeURIComponent(category)}`)} aria-label={tr('返回陪練課程', 'Back to training courses')}>
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" fontWeight={900}>{tr('LIVE 實戰演練', 'LIVE Practice')}</Typography>
            <Typography variant="caption" color="text.secondary">{tr('即時語音角色扮演', 'Real-time voice role-play')}</Typography>
          </Box>
          <Box sx={{ width: 40 }} />
        </Box>

        {loading ? (
          <Box sx={{ flex: 1, display: 'grid', placeItems: 'center', py: 10 }}><CircularProgress /></Box>
        ) : error ? (
          <Alert severity="error" sx={{ mt: 4 }}>{error}</Alert>
        ) : selected ? (
          <>
            <Box sx={{ mt: 4, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.05fr .95fr' }, gap: 3 }}>
              <Paper sx={{ position: 'relative', minHeight: { xs: 300, md: 520 }, borderRadius: 5, overflow: 'hidden', color: '#fff' }}>
                <Box component="img" src={selected.image} alt={nameOf(selected)} sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg,rgba(5,20,45,.02) 35%,rgba(5,20,45,.9) 100%)' }} />
                <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: 0, p: { xs: 3, md: 4 } }}>
                  <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                    <Chip label={tr('實戰演練', 'Live practice')} size="small" sx={{ bgcolor: '#1976d2', color: '#fff', fontWeight: 800 }} />
                    <Chip label={categoryOf(selected)} size="small" sx={{ bgcolor: 'rgba(255,255,255,.2)', color: '#fff' }} />
                    <Chip label={isEnglish ? `Difficulty: ${selected.difficulty_en || selected.difficulty}` : `難度：${selected.difficulty}`} size="small" sx={{ bgcolor: 'rgba(255,255,255,.2)', color: '#fff' }} />
                  </Box>
                  <Typography variant="h3" fontWeight={900}>{nameOf(selected)}</Typography>
                  <Typography variant="h6" sx={{ mt: .5, opacity: .92 }}>{localizeScenarioTitle(selected.title, selected.id, isEnglish)}</Typography>
                </Box>
              </Paper>

              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant="overline" color="primary" fontWeight={900}>{tr('選擇情境', 'Choose a scenario')}</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mt: 1 }}>
                  {scenarios.map((scenario) => {
                    const active = scenario.id === selected.id;
                    return (
                      <Paper
                        key={scenario.id}
                        component="button"
                        onClick={() => setSelectedId(scenario.id)}
                        variant="outlined"
                        sx={{
                          cursor: 'pointer',
                          textAlign: 'left',
                          p: 1.5,
                          borderRadius: 3,
                          bgcolor: active ? '#eaf2ff' : '#fff',
                          borderColor: active ? 'primary.main' : 'divider',
                          color: 'text.primary',
                        }}
                      >
                        <Typography fontWeight={850}>{nameOf(scenario)}</Typography>
                        <Typography variant="caption" color="text.secondary">{categoryOf(scenario)}</Typography>
                      </Paper>
                    );
                  })}
                </Box>

                <Typography variant="h5" fontWeight={900} sx={{ mt: 3 }}>{tr('情境說明', 'Scenario')}</Typography>
                {contentPending ? (
                  <CircularProgress size={22} sx={{ mt: 1.5 }} />
                ) : (
                  <Typography color="text.secondary" sx={{ mt: 1.25, lineHeight: 1.75 }}>{translateContent(selected.scene)}</Typography>
                )}

                <Typography variant="h6" fontWeight={900} sx={{ mt: 3 }}>{tr('本次任務', 'Tasks for this call')}</Typography>
                <Box sx={{ mt: 1.25, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {!contentPending && selected.tasks.map((task) => (
                    <Box key={task.id} sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
                      <CheckIcon color="primary" fontSize="small" sx={{ mt: .25 }} />
                      <Box>
                        <Typography fontWeight={750}>{translateContent(task.title)}</Typography>
                        <Typography variant="body2" color="text.secondary">{translateContent(task.goal)}</Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>

                <Button
                  variant="contained"
                  size="large"
                  startIcon={<PlayArrowIcon />}
                  onClick={() => setStarted(true)}
                  sx={{ mt: 'auto', pt: 1.6, pb: 1.6, borderRadius: 99, fontWeight: 900, fontSize: 17 }}
                >
                  {tr('開始 LIVE 實戰', 'Start LIVE practice')}
                </Button>
                <Typography variant="caption" color="text.secondary" align="center" sx={{ mt: 1.25 }}>
                  {tr('開始後瀏覽器會詢問麥克風權限', 'Your browser will ask for microphone permission when you start')}
                </Typography>
              </Box>
            </Box>
          </>
        ) : null}
      </Box>
    </Box>
  );
}
